import clsx from 'clsx';
import { useFormik } from 'formik';
import React from 'react';
import { Translation, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import QuestionIcon from '@/assets/question.svg';
import { Button } from '@/components/Elements';
import { Footer } from '@/components/Elements/Footer';
import { Tooltip } from '@/components/Elements/Tooltip';
import { iFormContactFields } from '@/components/Form/FormContact';
import { DEFAULT_LANG } from '@/config';
import { MAX_CHECKS_CIF } from '@/config/appConstants';
import { Country, CountryName } from '@/features/countries';
import { Province } from '@/features/provinces';
import { TinDocumentType } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { sendToPmpData, loadPmp } from '@/lib/Pmp/pmp';
import { AppLanguages, BaseFormEntity, BillingMode, Currency, ProductTypes } from '@/types';
import {
  checkCP,
  checkPhone,
  getFormName,
  renderField,
  renderLegalCheck,
  renderSelect,
} from '@/utils/forms';
import { getGuessURL, onlyDigits, sortCountries } from '@/utils/helpers';
import storage from '@/utils/storage';

export type FormUserAccountProps = {
  className?: string;
  onSubmit: (values: iFormUserAccountFields, product: ProductTypes | null) => void;
  countries: Country[];
  provinces: Province[];
  tinDocumentTypes: TinDocumentType[];
  countryOnChange: (formValues: any, countryId: string) => void;
  groupId: string;
  actionButton?: React.ReactNode | string;
  showCancelButton?: boolean;
  onCancelButton?: () => void;
  showFooter?: boolean;
  onRequestInfo?: () => void;
  onRequestInfoSubmit?: (values: iFormContactFields) => void;
  showPaymentCheckout?: boolean;
  product?: ProductTypes | null;
  currency: Currency | null;
  billingMode: BillingMode | null;
  showAgreementsCheck: boolean;
  isModalForm?: boolean;
};

export type iFormUserAccountFields = {
  groupId: string;
  name: string;
  lastname: string;
  countryId: string;
  provinceId: string;
  city: string;
  companyName: string;
  companyCif?: string;
  companyCifDocumentType?: string;
  address: string;
  postalCode: string;
  phone: string;
  currency: Currency | null;
  legalTerms: boolean;
  alreadySaved?: boolean;
  createdAt?: string;
  triesCif?: number;
} & BaseFormEntity;

const initialValuesDefault: iFormUserAccountFields = {
  formName: 'UserAccount',
  name: '',
  lastname: '',
  countryId: '',
  provinceId: '',
  city: '',
  companyName: '',
  companyCif: '',
  companyCifDocumentType: '',
  address: '',
  postalCode: '',
  phone: '',
  groupId: '',
  currency: null,
  legalTerms: false,
  alreadySaved: true,
};

export const FormUserAccount = ({
  onSubmit,
  className,
  countries,
  provinces,
  tinDocumentTypes,
  countryOnChange,
  groupId,
  actionButton,
  showCancelButton = false,
  onCancelButton,
  showFooter = false,
  showPaymentCheckout = false,
  onRequestInfo,
  product = null,
  currency,
  billingMode,
  showAgreementsCheck = false,
  isModalForm = false,
}: FormUserAccountProps) => {
  const { t, i18n } = useTranslation();
  const { getBillingDetails } = useGroupBillingDetails();

  const mapperInitialValues = (values?: iFormUserAccountFields) => {
    const initialValues = getBillingDetails();
    Object.assign(initialValues, values);
    if (
      initialValues?.triesCif &&
      initialValues?.triesCif >= MAX_CHECKS_CIF &&
      !initialValues.alreadySaved
    ) {
      initialValues && (initialValues.companyCif = '');
      initialValues && (initialValues.companyCifDocumentType = '');
    }

    initialValues && currency && (initialValues.currency = currency);
    initialValues && provinces && provinces.length == 0 && (initialValues.provinceId = '');
    initialValues &&
      tinDocumentTypes &&
      tinDocumentTypes.length == 0 &&
      (initialValues.companyCifDocumentType = '');

    return initialValues;
  };
  const initialValues = mapperInitialValues();

  initialValuesDefault.groupId = groupId;
  initialValuesDefault.currency = currency;
  const { getMe } = useMe();
  const me = getMe();
  countries = sortCountries(countries, i18n.language.toUpperCase() as keyof CountryName);

  const defaultValidationSchema = {
    name: Yup.string()
      .max(40, `${t('Forms.maxErrorChars')} 40 ${t('Forms.chars')}`)
      .required(t('Forms.required')),
    lastname: Yup.string()
      .max(40, `${t('Forms.maxErrorChars')} 40 ${t('Forms.chars')}`)
      .required(t('Forms.required')),
    companyName: Yup.string().required(t('Forms.required')),
    companyCif: Yup.string()
      .min(3, `${t('Forms.min')} 3 ${t('Forms.chars')}`)
      .max(20, `${t('Forms.maxErrorChars')} 20 ${t('Forms.chars')}`)
      .notOneOf(['null', '0', '00', '000'], t('Forms.invalidFormat'))
      .required(t('Forms.required')),
    companyCifDocumentType: Yup.string().required(t('Forms.required')),
    countryId: Yup.string().required(t('Forms.required')),
    provinceId: Yup.string().when('countryId', {
      is: 'ES',
      then: Yup.string().required(t('Forms.required')),
    }),
    address: Yup.string().required(t('Forms.required')),
    postalCode: Yup.string()
      .required(t('Forms.required'))
      .when(['countryId', 'provinceId'], {
        is: (countryId: string | undefined, provinceId: string | undefined) =>
          countryId === 'ES' && provinceId,
        then: Yup.string()
          .test(
            'isNumericCP',
            t('Forms.cpFormatNotValid'),
            (value: any) => value && onlyDigits(value)
          )
          .test(
            'isValidCPLength',
            t('Forms.cpLengthNotValid'),
            (value: any) => value && value.length === 5
          )
          .test('isValidCP', t('Forms.cpNotValid'), (val: any, formValues: any) => {
            const provinceId = formValues.parent.provinceId;
            return checkCP(val, provinceId);
          }),
      }),

    phone: Yup.string()
      .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
      .test('test-phone', t('Forms.invalidFormat'), checkPhone)
      .required(t('Forms.required')),
    city: Yup.string().required(t('Forms.required')),
    groupId: Yup.string().required(t('Forms.required')),
    legalTerms: showAgreementsCheck
      ? Yup.boolean().oneOf([true], t('Forms.legalRequired'))
      : Yup.boolean(),
  };

  const renderLabel = (language: string, item: Country) =>
    (item.name as any)[language.toUpperCase()];

  const setResignationAsViewed = () => {
    storage.setResignationViewed();
    window.location.assign(getGuessURL());
  };

  const getSignupFormResignation = () => (
    <Button variant="link--black" type="button" onClick={setResignationAsViewed} size="md">
      {t('Forms.registerResignation')}
    </Button>
  );

  const getCancelButton = () => (
    <Button
      variant="ghost"
      type="button"
      onClick={onCancelButton}
      size="md"
      className={clsx('mx-3.5', {
        '!w-1/2': isModalForm,
      })}
    >
      {t('Forms.cancelPendingProduct')}
    </Button>
  );

  const getSubmitButton = (formik: any) => (
    <Button
      id={getFormName(formik) + 'Button'}
      variant="primary"
      type="submit"
      size="md"
      className={clsx('mx-3.5', {
        'w-full': isModalForm && !showCancelButton,
        '!w-1/2': isModalForm && showCancelButton,
      })}
    >
      {actionButton || t('common.save')}
    </Button>
  );

  const getCifTooltip = () => {
    const getTrigger = () => (
      <img
        className="w-5 h-5 text-md-links"
        src={QuestionIcon}
        alt="Question"
        width="20"
        height="20"
      />
    );

    const onDefaultRequestInfoOpen = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onRequestInfo && onRequestInfo();
    };

    const getTooltipMessage = () => (
      <>
        <Translation>{(t) => t('Forms.CIFBlocked')}</Translation>
        <a
          title={t('Forms.contact')}
          className="ml-1 underline text-md-links hover:text-md-linksAlt hover:font-black"
          onClick={onDefaultRequestInfoOpen}
          href="#"
        >
          {t('Forms.contact')}
        </a>
        .
      </>
    );

    return (
      <Tooltip
        message={getTooltipMessage()}
        className="ml-2"
        trigger="click"
        tooltipClassName="bg-blue-100 text-blue-900 border-blue-200 px-3 pt-2 pb-4"
      >
        {getTrigger()}
      </Tooltip>
    );
  };

  const UserForm = ({ formik }: any) => {
    const countryOnDefaultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      countryOnChange(formik, e.target.value);
    };
    const provinceOnDefaultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      initialValues.provinceId = e.target.value;
    };

    const companyCifDocumentTypeOnDefaultChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      initialValues.companyCifDocumentType = e.target.value;
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      showAgreementsCheck && sendToPmpData(event);
      formik.handleSubmit(event);
    };
    const hasBlockedCifField = (): boolean => {
      if (initialValues?.triesCif && initialValues?.triesCif >= MAX_CHECKS_CIF) return true;
      return (!!initialValues?.companyCif &&
        !!initialValues?.companyCifDocumentType &&
        initialValues?.alreadySaved) as boolean;
    };
    const hasToShowResignationViewed = !initialValues?.currency && !storage.getResignationViewed();

    return (
      <form onSubmit={handleSubmit} className={className} id={getFormName(formik)}>
        <h3 className="md__payments__form--h3">{t('Forms.UserAccount.title')}</h3>

        {showPaymentCheckout && product && currency && billingMode && (
          <h6 className="mb-5">{t('Forms.UserAccount.checkoutProductPreviewNoShow')}</h6>
        )}
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderField(formik, 'name', { customClasses: 'w-full md:w-1/2', required: true })}
          {renderField(formik, 'lastname', { customClasses: 'w-full md:w-1/2', required: true })}
        </div>
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderSelect(formik, 'phone', {
            customClasses: 'w-full md:w-1/2',
            type: 'phone',
            required: true,
            renderLabel: (item: { name: string }) => item.name,
            defaultCountry: (initialValues?.countryId || i18n.language).toUpperCase(),
            userLanguage: (i18n.language || DEFAULT_LANG) as AppLanguages,
          })}
          {renderField(formik, 'address', { customClasses: 'w-full md:w-1/2', required: true })}
        </div>
        <div className="mb-3 justify-between gap-3 grid grid-cols-1 lg:grid-cols-2">
          {renderSelect(formik, 'countryId', {
            customClasses: 'w-full bg-md-white',
            itemClasses: 'bg-md-white',
            options: countries,
            renderLabel: (item: Country) => renderLabel(i18n.language, item),
            onChange: countryOnDefaultChange,
            value: initialValues?.countryId,
            required: true,
            disabled: hasBlockedCifField(),
          })}
          {renderSelect(formik, 'provinceId', {
            customClasses: 'w-full ',
            itemClasses: 'bg-md-white',
            options: provinces,
            value: provinces.length > 0 ? initialValues?.provinceId : '',
            onChange: provinceOnDefaultChange,
          })}
        </div>
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderField(formik, 'city', { customClasses: 'w-full md:w-1/2', required: true })}
          {renderField(formik, 'postalCode', { customClasses: 'w-full md:w-1/2', required: true })}
        </div>

        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderField(formik, 'companyName', { customClasses: 'w-full', required: true })}
        </div>
        <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
          {renderSelect(formik, 'companyCifDocumentType', {
            customClasses: 'w-full md:w-1/2',
            required: true,
            disabled: hasBlockedCifField(),
            helpText: hasBlockedCifField() ? getCifTooltip() : '',
            itemClasses: 'bg-md-white',
            options: tinDocumentTypes,
            value: tinDocumentTypes.length > 0 ? initialValues?.companyCifDocumentType : '',
            onChange: companyCifDocumentTypeOnDefaultChange,
          })}
          {renderField(formik, 'companyCif', {
            customClasses: 'w-full md:w-1/2',
            required: true,
            disabled: hasBlockedCifField(),
            helpText: hasBlockedCifField() ? getCifTooltip() : '',
          })}
        </div>

        {renderField(formik, 'groupId', { type: 'hidden' })}
        {renderField(formik, 'email', { type: 'hidden', value: me.userInfo.email })}
        {renderField(formik, 'currency', { type: 'hidden', value: currency })}
        {showAgreementsCheck &&
          renderLegalCheck(formik, 'legalTerms', {
            required: true,
            renderLabel: () =>
              t('LegalTerms.checkCps')
                .replace('{{urlTerms}}', t('LegalTerms.termsURL'))
                .replace('{{privacyPolicyURL}}', t('LegalTerms.privacyPolicyURL')),
          })}
        {showAgreementsCheck && loadPmp()}
        <div className="mt-10 mb-3 flex justify-center lg:justify-between flex-wrap">
          <div className="flex items-center mb-10 lg:mb-0 justify-end w-full">
            {!isModalForm && hasToShowResignationViewed && getSignupFormResignation()}
            {getSubmitButton(formik)}
            {isModalForm && showCancelButton && getCancelButton()}
          </div>
          {isModalForm && hasToShowResignationViewed && getSignupFormResignation()}
        </div>
        {showFooter && onRequestInfo && <Footer onRequestInfo={onRequestInfo} />}
      </form>
    );
  };

  const onFormSubmit = (values: iFormUserAccountFields) => {
    values.triesCif = values?.triesCif
      ? parseInt(values?.triesCif as unknown as string, 10) + 1
      : 1;
    mapperInitialValues(values);
    if (
      initialValues?.companyCif &&
      initialValues?.companyCifDocumentType &&
      initialValues?.alreadySaved
    ) {
      delete values.companyCif;
      delete values.companyCifDocumentType;
    }
    onSubmit && onSubmit(values, product);
  };

  const MainForm = () => {
    const formikData = {
      initialValues: { ...initialValuesDefault, ...initialValues },
      validationSchema: Yup.object(defaultValidationSchema),
      onSubmit: onFormSubmit,
    };

    return <UserForm formik={useFormik(formikData)} />;
  };

  return (
    <div className="w-full m-auto overflow px-4 py-0 lg:py-6">
      <MainForm />
    </div>
  );
};
